import { useCallback, useEffect, useSyncExternalStore } from 'react';

/**
 * Home-screen install (Progressive Web App). Riders, merchants and admins each install their own app: separate
 * name, icon and colour, opening straight into /rider, /merchant or /admin.
 */
export type AppRole = 'merchant' | 'rider' | 'admin';

export const APP_INFO: Record<AppRole | 'app', { name: string; theme: string; manifest: string; apple: string }> = {
  app: { name: 'iHatod', theme: '#1f6b34', manifest: '/manifest.webmanifest', apple: '/icons/app-apple-180.png' },
  merchant: { name: 'iHatod Shop', theme: '#1f6b34', manifest: '/manifest-merchant.webmanifest', apple: '/icons/merchant-apple-180.png' },
  rider: { name: 'iHatod Rider', theme: '#1f6b34', manifest: '/manifest-rider.webmanifest', apple: '/icons/rider-apple-180.png' },
  admin: { name: 'iHatod Admin', theme: '#0f172a', manifest: '/manifest-admin.webmanifest', apple: '/icons/admin-apple-180.png' },
};

/* ---------------------------------------------------------------- service worker */

/** Registers the service worker in production builds (it is what makes the app installable and gives the offline page). */
export function registerServiceWorker(): void {
  if (!__SW_ENABLED__ || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined); // installing is a nicety: the site works the same without it
  });
}

/* ---------------------------------------------------------------- which app is this page for */

function setAttr(selector: string, attr: string, value: string): void {
  document.head.querySelector(selector)?.setAttribute(attr, value);
}

/** Point the page's manifest, icon and colours at one role's app, so "Add to Home screen" installs the right one. */
export function setAppRole(role: AppRole | null): void {
  const a = APP_INFO[role ?? 'app'];
  setAttr('link[rel="manifest"]', 'href', a.manifest);
  setAttr('link[rel="apple-touch-icon"]', 'href', a.apple);
  setAttr('meta[name="theme-color"]', 'content', a.theme);
  setAttr('meta[name="apple-mobile-web-app-title"]', 'content', a.name);
}

export function useAppRole(role: AppRole | null): void {
  useEffect(() => setAppRole(role), [role]);
}

/* ---------------------------------------------------------------- install prompt */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** Start listening as early as possible: the browser fires this once, soon after the page loads. */
export function captureInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // keep the event so our own button can show the browser's install dialog
    deferred = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    installed = true;
    deferred = null;
    emit();
  });
}

const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export interface InstallState {
  /** already running as an installed app */
  standalone: boolean;
  /** the browser can show its own install dialog */
  canPrompt: boolean;
  /** iPhone / iPad: installing is done by hand from the Share menu */
  ios: boolean;
  install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
}

const snapshot = () => `${deferred ? 1 : 0}${installed ? 1 : 0}`;
const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

export function useInstall(): InstallState {
  useSyncExternalStore(subscribe, snapshot, () => '00');
  const install = useCallback(async () => {
    if (!deferred) return 'unavailable' as const;
    const ev = deferred;
    deferred = null;
    emit();
    await ev.prompt();
    return (await ev.userChoice).outcome;
  }, []);
  return { standalone: installed || isStandalone(), canPrompt: !!deferred, ios: isIos(), install };
}
